import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Switch, FormControlLabel,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Typography
} from "@material-ui/core";
import API from "../../../../network/dashboardAPI";
import useAlert from "../../../../services/useAlert";
import PickupPointMap from "./PickupPointMap";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const defaultSchedule = () => DAYS.map((day) => ({ day, open: "08:00", close: "17:00", closed: false }));

const readSchedule = (openingHours) => {
  let hours = openingHours;
  if (typeof hours === "string") {
    try { hours = JSON.parse(hours); } catch (error) { hours = null; }
  }
  if (!Array.isArray(hours?.days)) return defaultSchedule();
  return DAYS.map((day) => {
    const entry = hours.days.find((item) => item.day === day);
    return { day, open: entry?.open || "08:00", close: entry?.close || "17:00", closed: !entry };
  });
};

export default function PickupPointDialog({ open, onClose, onSaved, initialData, deliveryCompanies }) {
  const alert = useAlert();
  const isEdit = !!initialData?.id;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", delivery_company_id: "", address: "",
    phone: "", latitude: "", longitude: "", has_cool_storage: false,
    is_active: true,
  });
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [errors, setErrors] = useState({});

  // Default to Phnom Penh center for new pickup points
  const defaultCenter = { lat: 11.5564, lng: 104.9282 };

  useEffect(() => {
    if (open) {
      if (initialData) {
        setSchedule(readSchedule(initialData.opening_hours));
        setForm({
          name: initialData.name || "",
          description: initialData.description || "",
          delivery_company_id: initialData.delivery_company_id || "",
          address: initialData.address || "",
          phone: initialData.phone || "",
          latitude: initialData.latitude || "",
          longitude: initialData.longitude || "",
          has_cool_storage: initialData.has_cool_storage || false,
          is_active: initialData.is_active !== undefined ? initialData.is_active : true,
        });
      } else {
        setSchedule(defaultSchedule());
        setForm({
          name: "", description: "", delivery_company_id: "", address: "",
          phone: "", latitude: "", longitude: "", has_cool_storage: false,
          is_active: true,
        });
      }
      setErrors({});
    }
  }, [open, initialData]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.delivery_company_id) errs.delivery_company_id = "Delivery company is required";
    if (!form.latitude) errs.latitude = "Latitude is required";
    if (!form.longitude) errs.longitude = "Longitude is required";
    schedule.forEach(({ day, open, close, closed }) => {
      if (!closed && (!open || !close || open >= close)) {
        errs[`hours_${day}`] = "Closing time must be later than opening time";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        delivery_company_id: parseInt(form.delivery_company_id),
        opening_hours: {
          days: schedule.filter((entry) => !entry.closed).map(({ day, open, close }) => ({ day, open, close })),
        },
      };
      let response;
      if (isEdit) {
        payload.id = initialData.id;
        response = await API.updatePickupPoint(payload);
      } else {
        response = await API.addPickupPoint(payload);
      }
      const res = await response.json();
      if (res.success) {
        alert.toast(isEdit ? "Pickup point updated" : "Pickup point created", "success");
        onSaved();
        onClose();
      } else {
        alert.error("Error", res.data || "Failed to save");
      }
    } catch (error) {
      alert.error("Error", "Failed to save pickup point");
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleMapLocationSelect = (lat, lng) => {
    setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    if (errors.latitude) setErrors((prev) => ({ ...prev, latitude: undefined }));
    if (errors.longitude) setErrors((prev) => ({ ...prev, longitude: undefined }));
  };

  const handleScheduleChange = (day, field, value) => {
    setSchedule((prev) => prev.map((entry) => entry.day === day ? { ...entry, [field]: value } : entry));
    if (errors[`hours_${day}`]) {
      setErrors((prev) => ({ ...prev, [`hours_${day}`]: undefined }));
    }
  };

  const mapCenter = form.latitude && form.longitude
    ? { lat: parseFloat(form.latitude), lng: parseFloat(form.longitude) }
    : defaultCenter;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEdit ? "Edit Pickup Point" : "Add Pickup Point"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Name *"
              fullWidth
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" size="small" fullWidth error={!!errors.delivery_company_id}>
              <InputLabel>Delivery Company *</InputLabel>
              <Select
                value={form.delivery_company_id}
                onChange={(e) => handleChange("delivery_company_id", e.target.value)}
                label="Delivery Company *"
              >
                {deliveryCompanies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                ))}
              </Select>
              {errors.delivery_company_id && (
                <Typography variant="caption" color="error">{errors.delivery_company_id}</Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Address"
              fullWidth
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Phone"
              fullWidth
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Opening Hours</Typography>
            {schedule.map(({ day, open, close, closed }) => (
              <Grid container spacing={1} alignItems="center" key={day} style={{ marginBottom: 8 }}>
                <Grid item xs={3} sm={2}><Typography>{day}</Typography></Grid>
                <Grid item xs={3} sm={2}>
                  <FormControlLabel
                    control={<Switch checked={!closed} onChange={(e) => handleScheduleChange(day, "closed", !e.target.checked)} color="primary" />}
                    label="Open"
                  />
                </Grid>
                <Grid item xs={3} sm={4}>
                  <TextField label="Opening" type="time" fullWidth disabled={closed} value={open}
                    onChange={(e) => handleScheduleChange(day, "open", e.target.value)}
                    InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
                </Grid>
                <Grid item xs={3} sm={4}>
                  <TextField label="Closing" type="time" fullWidth disabled={closed} value={close}
                    onChange={(e) => handleScheduleChange(day, "close", e.target.value)}
                    error={!!errors[`hours_${day}`]} helperText={errors[`hours_${day}`]}
                    InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom style={{ marginBottom: 4 }}>
              Location * — Drag the map to position the pin
            </Typography>
            <PickupPointMap
              latitude={mapCenter.lat}
              longitude={mapCenter.lng}
              onLocationSelect={handleMapLocationSelect}
            />
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={6}>
                <TextField
                  label="Latitude *"
                  fullWidth
                  type="number"
                  value={form.latitude}
                  onChange={(e) => handleChange("latitude", e.target.value)}
                  error={!!errors.latitude}
                  helperText={errors.latitude || "Drag map to position pin"}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Longitude *"
                  fullWidth
                  type="number"
                  value={form.longitude}
                  onChange={(e) => handleChange("longitude", e.target.value)}
                  error={!!errors.longitude}
                  helperText={errors.longitude || "Drag map to position pin"}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.has_cool_storage}
                  onChange={(e) => handleChange("has_cool_storage", e.target.checked)}
                  color="primary"
                />
              }
              label="Has Cool Storage"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
